import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Avatar,
  Badge,
  Box,
  Stack,
  SvgIcon,
  Tabs,
  Tab,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/NotificationsNone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAuth from 'src/context/AuthContext';
import ROUTE from 'src/utils/routeConstants';
import { FLEET_ONLY_BRAND, isFleetOnlyModeEnabled } from 'src/utils/appMode';
import { USER_ROLES } from 'src/utils/constants';
import ProfileEditorDialog from 'src/components/account/ProfileEditorDialog';
import PageHeaderContext from '../context/PageHeaderContext';

export const SIDE_NAV_WIDTH = 280;
export const COLLAPSED_SIDE_NAV_WIDTH = 80;
const TOP_NAV_HEIGHT = 64;
const MOBILE_TOP_NAV_HEIGHT = 64;

// Helper to get initials safely
const getInitials = (name = '') => name
  .replace(/\s+/, ' ')
  .split(' ')
  .slice(0, 2)
  .map((v) => v && v[0].toUpperCase())
  .join('');

const DashboardNavbar = ({
  onNavOpen,
  onNavClose,
  onToggleCollapse,
  isCollapsed,
  sideNavWidth,
  mobileNavOpen = false,
  transactionPage = false
}) => {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);
  const { unreadCount } = useSelector((state) => state.notifications);
  const { appSettings } = useSelector((state) => state.settings);
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);
  const history = useHistory();
  const location = useLocation();
  const { getUser } = useAuth();
  const { pageHeader } = useContext(PageHeaderContext);
  const user = getUser();
  const effectiveWidth = lgUp ? (sideNavWidth ?? SIDE_NAV_WIDTH) : 0;
  const isOperator = user?.role === USER_ROLES.operator;
  const showAnalyticsTabs = !!appSettings?.fleet_management
    && location.pathname === ROUTE.dashboard
    && !isFleetOnlyMode;
  const analyticsTabValue = new URLSearchParams(location.search).get('analytics') === 'fleet'
    ? 'fleet'
    : 'fuel';

  const userDisplayName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';
  const userInitials = getInitials(userDisplayName);
  let navbarBackground = `linear-gradient(90deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.default, 0.8)})`;
  let navbarBorder = `1px solid ${alpha(theme.palette.divider, 0.1)}`;
  let headerControlColor = 'text.secondary';
  if (isFleetOnlyMode) {
    navbarBackground = `linear-gradient(90deg, ${alpha(FLEET_ONLY_BRAND.canvas, 0.96)}, ${alpha('#EAF2F8', 0.92)})`;
    navbarBorder = `1px solid ${FLEET_ONLY_BRAND.border}`;
    headerControlColor = FLEET_ONLY_BRAND.ink;
  }
  if (transactionPage) {
    navbarBackground = [
      'linear-gradient(132deg, transparent 0%, transparent 68%, rgba(18, 107, 165, 0.38) 68%, rgba(18, 107, 165, 0.38) 73%, transparent 73%, transparent 78%, rgba(20, 126, 184, 0.32) 78%, rgba(20, 126, 184, 0.32) 84%, transparent 84%)',
      'linear-gradient(132deg, transparent 0%, transparent 76%, rgba(30, 145, 196, 0.22) 76%, rgba(30, 145, 196, 0.22) 88%, transparent 88%)',
      'linear-gradient(135deg, #0c467b 0%, #0d4275 62%, #0a3968 100%)'
    ].join(', ');
    navbarBorder = 'none';
    headerControlColor = '#ffffff';
  }
  let avatarBackground = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;
  if (isFleetOnlyMode) {
    avatarBackground = `linear-gradient(135deg, ${FLEET_ONLY_BRAND.primary} 0%, ${FLEET_ONLY_BRAND.primarySoft} 100%)`;
  }
  if (appSettings?.company_logo_url) {
    avatarBackground = '#FFFFFF';
  }

  const handleProfileEditorOpen = () => {
    setProfileEditorOpen(true);
  };

  const handleProfileEditorClose = () => setProfileEditorOpen(false);

  const handleAnalyticsTabChange = (event, newValue) => {
    if (!newValue) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    if (newValue === 'fleet') {
      searchParams.set('analytics', 'fleet');
    } else {
      searchParams.delete('analytics');
    }

    const search = searchParams.toString();
    history.push({
      pathname: ROUTE.dashboard,
      search: search ? `?${search}` : ''
    });
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          backdropFilter: 'blur(20px)',
          background: navbarBackground,
          position: 'sticky',
          left: { lg: `${effectiveWidth}px` },
          top: 0,
          width: { lg: `calc(100% - ${effectiveWidth}px)` },
          zIndex: theme.zIndex.appBar + 1,
          borderBottom: navbarBorder,
          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), left 300ms cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{
            minHeight: {
              xs: showAnalyticsTabs ? 112 : MOBILE_TOP_NAV_HEIGHT,
              sm: TOP_NAV_HEIGHT
            },
            flexWrap: {
              xs: showAnalyticsTabs ? 'wrap' : 'nowrap',
              md: 'nowrap'
            },
            rowGap: showAnalyticsTabs ? 1.5 : 0,
            px: {
              xs: 2,
              sm: 3
            },
            pl: transactionPage ? { xs: 1, sm: 1.5 } : undefined,
            py: showAnalyticsTabs ? 1 : 0,
            position: 'relative'
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              order: 1,
              '& .MuiIconButton-root': {
                color: transactionPage ? '#ffffff' : undefined,
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }
            }}
          >
            {!lgUp && (
              <IconButton
                onClick={onNavOpen}
                sx={{ color: headerControlColor }}
              >
                <MenuOpenIcon
                  sx={{
                    fontSize: transactionPage ? 28 : undefined,
                    transform: mobileNavOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            )}
            {lgUp && (
              <IconButton
                onClick={onToggleCollapse}
                sx={{ color: headerControlColor }}
              >
                <MenuOpenIcon
                  sx={{
                    fontSize: transactionPage ? 28 : undefined,
                    transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            )}
          </Stack>

          {pageHeader && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={{ xs: 1, sm: 2 }}
              sx={{
                order: 2,
                flex: '1 1 auto',
                minWidth: 0,
                ml: { xs: 0.5, sm: 1 },
                ...(transactionPage && {
                  borderLeft: '4px solid #27b9ec',
                  pl: { xs: 1, sm: 1.5 }
                })
              }}
            >
              <Stack
                direction="column"
                alignItems="flex-start"
                justifyContent="center"
                spacing={0}
                sx={{ minWidth: 0, overflow: 'hidden' }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontWeight: 800,
                    color: transactionPage ? '#ffffff' : 'inherit',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.1
                  }}
                >
                  {pageHeader.title}
                </Box>
                {pageHeader.subtitle && (
                  <Box
                    component="span"
                    sx={{
                      color: transactionPage ? '#75d5ff' : 'text.secondary',
                      fontSize: { xs: '0.72rem', sm: '0.875rem' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.2
                    }}
                  >
                    {pageHeader.subtitle}
                  </Box>
                )}
              </Stack>
            </Stack>
          )}

          {showAnalyticsTabs && (
            <Box
              sx={{
                order: { xs: 3, md: 2 },
                width: { xs: '100%', md: 'auto' },
                display: 'flex',
                justifyContent: 'center',
                position: { md: 'absolute' },
                left: { md: '50%' },
                transform: { md: 'translateX(-50%)' }
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                  p: 0.5,
                  borderRadius: 3,
                  boxShadow: '0 2px 10px 0 rgba(0,0,0,0.04)',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Tabs
                  value={analyticsTabValue}
                  onChange={handleAnalyticsTabChange}
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  sx={{
                    minHeight: 38,
                    '& .MuiTab-root': {
                      minHeight: 38,
                      minWidth: { xs: 118, sm: 138 },
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      borderRadius: 2,
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      color: '#0b6e99',
                      '&:hover': {
                        color: '#095a7d',
                        bgcolor: alpha(theme.palette.action.hover, 0.5)
                      },
                      '&.Mui-selected': {
                        color: '#0b6e99',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)'
                      }
                    }
                  }}
                  aria-label="Dashboard Analytics Tabs"
                >
                  <Tab value="fuel" label="Fuel Analytics" disableRipple />
                  <Tab value="fleet" label="Fleet Analytics" disableRipple />
                </Tabs>
              </Box>
            </Box>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ order: pageHeader ? 3 : 2, ml: 'auto' }}
          >
            {!lgUp && mobileNavOpen && (
              <IconButton
                onClick={onNavClose}
                sx={{ color: isFleetOnlyMode ? FLEET_ONLY_BRAND.primary : 'error.main' }}
              >
                <CloseIcon />
              </IconButton>
            )}
            {!isOperator && (
              <Tooltip title="Notifications">
                <IconButton
                  sx={{ color: headerControlColor }}
                  onClick={() => history.push(ROUTE.notifications)}
                >
                  <Badge
                    badgeContent={unreadCount > 0 ? unreadCount : null}
                    color="error"
                  >
                    <SvgIcon fontSize="medium" sx={{ fontSize: transactionPage ? 28 : undefined }}>
                      <NotificationsIcon />
                    </SvgIcon>
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* Enhanced User Profile Icon */}
            <Tooltip title="Profile">
              <Box
                onClick={handleProfileEditorOpen}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative'
                }}
              >
                {/* Online Status Badge */}
                <Badge
                  overlap="rectangular"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                  variant="dot"
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#44b700',
                      color: '#44b700',
                      boxShadow: `0 0 0 2px ${isFleetOnlyMode ? FLEET_ONLY_BRAND.canvas : theme.palette.background.paper}`,
                      '&::after': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        animation: 'ripple 1.2s infinite ease-in-out',
                        border: '1px solid currentColor',
                        content: '""'
                      }
                    },
                    '@keyframes ripple': {
                      '0%': {
                        transform: 'scale(.8)',
                        opacity: 1
                      },
                      '100%': {
                        transform: 'scale(2.4)',
                        opacity: 0
                      }
                    }
                  }}
                >
                  <Avatar
                    src={appSettings?.company_logo_url || undefined}
                    alt={appSettings?.company_name || userDisplayName || 'Profile'}
                    sx={{
                      height: 44,
                      width: 44,
                      // Gradient Background
                      background: avatarBackground,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: '#ffffff',
                      p: 0.5,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid transparent',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: isFleetOnlyMode
                          ? `0 0 0 4px ${alpha(FLEET_ONLY_BRAND.primarySoft, 0.18)}`
                          : `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
                        transform: 'scale(1.05)'
                      },
                      '& .MuiAvatar-img': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: 1
                      }
                    }}
                  >
                    {userInitials || <PersonIcon />}
                  </Avatar>
                </Badge>
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      <ProfileEditorDialog
        open={profileEditorOpen}
        onClose={handleProfileEditorClose}
      />
    </>
  );
};

DashboardNavbar.propTypes = {
  onNavOpen: PropTypes.func,
  onNavClose: PropTypes.func,
  onToggleCollapse: PropTypes.func,
  isCollapsed: PropTypes.bool,
  sideNavWidth: PropTypes.number,
  mobileNavOpen: PropTypes.bool,
  transactionPage: PropTypes.bool
};

export default DashboardNavbar;
